import type { MapRef } from "@vis.gl/react-maplibre";
import { LngLatBounds } from "maplibre-gl";
import { useEffect, useMemo, useRef, type RefObject } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useCompanyScopeContext } from "@ryde/redux";
import {
  CORRECTING_FIX_BOUNDS_TIMEOUT,
  ILocationInfo,
  isLocationInfoWithValidMapCoordinates,
  LANG_DEFAULT,
  MAP_PAD_RATIO,
  PAD_AROUND_DIMENSION,
  profileDtoAtom,
  ResizeMapEvent,
  SupportedLanguage,
} from "@ryde/shared";

import {
  fitMapLibreBoundsSafely,
  hasUsableMapContainer,
  padLngLatBounds,
} from "./maplibre-bounds.utils";
import { getAliveMap, resizeMapLibreIfLaidOut } from "./maplibre-map.utils";

interface IUseMapLibreLocationsAutoFitArgs {
  mapRef: RefObject<MapRef | null>;
  /** Points the map should frame. An empty list only keeps the canvas sized; it never re-frames. */
  locations: ILocationInfo[];
  /** Gate: `fitBounds` before the style loads is dropped by MapLibre, so fits wait for this. */
  isMapLoaded: boolean;
  /**
   * Which splitter/panel resize this map listens to. Opt in: a map subscribes only to its own screen's
   * event. Leave unset when the map sits on no splitter, or when another fit on the same map already owns
   * the subscription and the two must not both resize it.
   */
  resizeEvent?: ResizeMapEvent | null;
  /** Screen padding (px) left around the fitted bounds. Defaults to the ride maps' value. */
  fitPadding?: number;
  /** How far to grow the bounds beyond the points, as a ratio of their own span. */
  boundsPadRatio?: number;
}

/**
 * Frames a MapLibre map on a set of locations — the MapLibre twin of the Leaflet `useMapStationsAutoFit`,
 * used by every non-route map (passengers, stations, areas, the address maps).
 *
 * Keeps the Leaflet behaviour: pad the bounds by `MAP_PAD_RATIO`, fit with `PAD_AROUND_DIMENSION` of screen
 * padding, and re-size the canvas when the page's splitter fires its resize event.
 *
 * What Leaflet did not need: a fit can arrive before the map can honour it (style still loading, container
 * not laid out yet — `fitBounds` on a 0×0 container computes a nonsense zoom). Those bounds are parked in
 * `pendingBoundsRef` and completed by the next resize, rather than being silently dropped.
 */
export const useMapLibreLocationsAutoFit = ({
  mapRef,
  locations,
  isMapLoaded,
  resizeEvent = null,
  fitPadding = PAD_AROUND_DIMENSION,
  boundsPadRatio = MAP_PAD_RATIO,
}: IUseMapLibreLocationsAutoFitArgs): void => {
  const { eventEmitter } = useCompanyScopeContext();
  const profile = useRecoilValue(profileDtoAtom);
  const pendingBoundsRef = useRef<LngLatBounds | undefined>(undefined);
  const langRef = useRef<SupportedLanguage | undefined>(undefined);

  /*
   * Extends the bounds point by point rather than going through `getBoundMapFromLocations`, whose
   * de-duplication is O(n²) — it calls `bounds.some(...)` for every location. That is unnoticeable for a
   * list of stations and catastrophic for a polygon: showing one area on the stations map feeds thousands
   * of vertices through it and locks the main thread for seconds.
   *
   * Dropping the de-duplication changes nothing about the result. Duplicate coordinates cannot move a
   * bounding box, so the extra work was never buying anything here.
   */
  const boundsComputed = useMemo(() => {
    const bounds = new LngLatBounds();
    let hasAnyPoint = false;

    locations.forEach(location => {
      if (!isLocationInfoWithValidMapCoordinates(location)) {
        return;
      }

      bounds.extend([location.longitude, location.latitude]);
      hasAnyPoint = true;
    });

    if (!hasAnyPoint) {
      return undefined;
    }

    return padLngLatBounds(bounds, boundsPadRatio);
  }, [locations, boundsPadRatio]);

  const fitBounds = useEvent(
    (
      bounds: LngLatBounds | undefined,
      options?: { resize?: boolean },
    ): void => {
      const map = getAliveMap(mapRef.current);

      if (!map || !bounds) {
        return;
      }

      if (!isMapLoaded || !hasUsableMapContainer(mapRef)) {
        pendingBoundsRef.current = bounds;
        return;
      }

      if (options?.resize) {
        resizeMapLibreIfLaidOut(map);
      }

      if (
        fitMapLibreBoundsSafely({
          map,
          bounds,
          padding: fitPadding,
        })
      ) {
        pendingBoundsRef.current = undefined;
        return;
      }

      // Same park as ride autofit: a silent NaN restore must retry on the next resize, not look "fitted".
      pendingBoundsRef.current = bounds;
    },
  );

  const resizeMap = useEvent((): void => {
    resizeMapLibreIfLaidOut(getAliveMap(mapRef.current));

    // Only finishes a fit that was already requested and could not run. It deliberately does not re-fit to
    // the current locations, which would throw away whatever the user has panned to on every splitter drag.
    if (pendingBoundsRef.current) {
      fitBounds(pendingBoundsRef.current);
    }
  });

  useEffect(() => {
    if (!resizeEvent) {
      return;
    }

    eventEmitter?.on(resizeEvent, resizeMap);

    return () => {
      eventEmitter?.off(resizeEvent, resizeMap);
    };
  }, [eventEmitter, resizeEvent, resizeMap]);

  useEffect(() => {
    // Deferred a tick, as the Leaflet hook did: the container is often still being laid out on the render
    // that produced these bounds.
    const timeoutId = setTimeout(() => fitBounds(boundsComputed));

    return () => clearTimeout(timeoutId);
  }, [boundsComputed, fitBounds, isMapLoaded]);

  // A Hebrew/English switch flips the layout direction, which can resize the map's container. Re-fit once
  // the new layout has settled so the framing survives the flip.
  useEffect(() => {
    const language = profile?.metadata?.language || LANG_DEFAULT;
    const hasLanguageChanged = Boolean(
      langRef.current && langRef.current !== language,
    );

    langRef.current = language;

    if (!hasLanguageChanged) {
      return;
    }

    const timeoutId = setTimeout(
      () => fitBounds(boundsComputed, { resize: true }),
      CORRECTING_FIX_BOUNDS_TIMEOUT,
    );

    return () => clearTimeout(timeoutId);
  }, [boundsComputed, fitBounds, profile?.metadata?.language]);
};
